import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ArrowLeft } from "lucide-react";
import { bLabel } from "./parts";

const YEAR_COLORS = ["#1F3D34", "#7C9673", "#B8860B", "#7C3AED", "#0E7490", "#9F1239"];
const MONTH_TICKS = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const doyToMonth = (d) => {
  let i = MONTH_TICKS.findIndex((t, k) => d >= t && (k === 11 || d < MONTH_TICKS[k + 1]));
  return MONTHS[Math.max(i, 0)];
};

export default function SeasonTimeline() {
  const { ranchId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/scouting/timeline/${ranchId}`).then(({ data }) => setData(data));
  }, [ranchId]);

  if (!data) return <div className="p-8 font-bold uppercase text-sm">Loading…</div>;
  const { ranch, points, years } = data;

  const growthPoints = points.filter((p) => p.shoot_length_in != null);
  const chartMap = new Map();
  growthPoints.forEach((p) => {
    const row = chartMap.get(p.doy) || { doy: p.doy };
    row[p.year] = p.shoot_length_in;
    chartMap.set(p.doy, row);
  });
  const chartData = [...chartMap.values()].sort((a, b) => a.doy - b.doy);
  const phenoByYear = years.map((y) => ({ year: y, items: points.filter((p) => p.year === y && p.phenology) }));

  return (
    <div className="space-y-5 max-w-5xl" data-testid="season-timeline">
      <button onClick={() => navigate(-1)} className="h-11 px-4 border border-stone-300 bg-white font-bold uppercase text-xs flex items-center gap-2"><ArrowLeft size={15} /> Back</button>
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
          Season Timeline — {ranch.block ? bLabel(ranch.block) : ranch.name}
        </h1>
        <p className="text-stone-500 text-sm mt-1">{[ranch.name, ranch.variety, ranch.acres ? `${ranch.acres} ac` : null].filter(Boolean).join(" · ")}</p>
      </div>

      {points.length === 0 ? (
        <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm">No scouting history yet for this block</div>
      ) : (
        <>
          {/* Shoot growth chart */}
          <div className="bg-white border border-stone-200 p-4">
            <div className="font-bold uppercase text-sm mb-3">Shoot Growth (inches) — Year Over Year</div>
            {growthPoints.length === 0 ? (
              <div className="text-stone-400 text-sm font-bold uppercase text-center py-8">No shoot length measurements yet</div>
            ) : (
              <div className="h-72" data-testid="growth-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis type="number" dataKey="doy" domain={["dataMin - 10", "dataMax + 10"]} ticks={MONTH_TICKS}
                      tickFormatter={doyToMonth} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit='"' />
                    <Tooltip labelFormatter={(d) => `~${doyToMonth(d)} (day ${d})`} formatter={(v, name) => [`${v}"`, name]} />
                    <Legend />
                    {years.map((y, i) => (
                      <Line key={y} type="monotone" dataKey={y} name={String(y)} stroke={YEAR_COLORS[i % YEAR_COLORS.length]}
                        strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Phenology tracks */}
          <div className="bg-white border border-stone-200 p-4">
            <div className="font-bold uppercase text-sm mb-3">Phenology Progression</div>
            <div className="space-y-5">
              {phenoByYear.map(({ year, items }, yi) => (
                <div key={year}>
                  <div className="text-xs font-bold mb-1" style={{ color: YEAR_COLORS[yi % YEAR_COLORS.length] }}>{year}</div>
                  {items.length === 0 ? (
                    <div className="text-xs text-stone-400">No phenology recorded</div>
                  ) : (
                    <div className="relative h-14 bg-stone-50 border border-stone-200">
                      {MONTH_TICKS.map((t, i) => (
                        <div key={t} className="absolute top-0 bottom-0 border-l border-stone-200/70" style={{ left: `${(t / 366) * 100}%` }}>
                          <span className="absolute top-0.5 left-0.5 text-[8px] text-stone-400">{MONTHS[i]}</span>
                        </div>
                      ))}
                      {items.map((p, i) => (
                        <div key={`${p.date}-${p.phenology || i}`} className="absolute" style={{ left: `${(p.doy / 366) * 100}%`, top: i % 2 === 0 ? "35%" : "60%" }} title={`${p.date}: ${p.phenology}`}>
                          <div className="w-2.5 h-2.5 -ml-1 rounded-full" style={{ background: YEAR_COLORS[yi % YEAR_COLORS.length] }} />
                          <div className="text-[9px] font-semibold whitespace-nowrap -ml-1 mt-0.5">{p.phenology}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Observation log */}
          <div className="bg-white border border-stone-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1F3D34] text-white text-xs uppercase">
                <tr>{["Date", "Phenology", "Shoot Length", "Growth", "Vigor"].map((h) => <th key={h} className="p-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {[...points].reverse().map((p, i) => (
                  <tr key={`${p.date}-${i}`} className="border-b border-stone-100 odd:bg-stone-50/50">
                    <td className="p-2.5 font-mono">{p.date}</td>
                    <td className="p-2.5">{p.phenology || "—"}</td>
                    <td className="p-2.5 font-mono">{p.shoot_length_in != null ? `${p.shoot_length_in}"` : "—"}</td>
                    <td className="p-2.5">{p.growth_status || "—"}</td>
                    <td className="p-2.5">{p.vigor || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
